import { useTranslate } from '@refinedev/core';
import { Card, Divider, Flex, Space, Tag, Typography, theme } from 'antd';
import { PhoneFrame } from '@/components/mobile_preview/PhoneFrame';
import { MobilePreviewDisclaimer } from '@/components/mobile_preview/MobilePreviewDisclaimer';
import { MobilePreviewProvider } from '@/components/mobile_preview/MobilePreviewProvider';
import { MobileThemeToggle } from '@/components/mobile_preview/MobileThemeToggle';
import { PreviewAlertList } from '@/components/mobile_preview/PreviewAlertList';
import { asDeviceWidth, GUTTER } from '@/components/mobile_preview/constants';
import { CreativeSlot } from '@/components/user_catalog/creative/CreativeSlot';
import { ratioLabel } from '@/components/user_catalog/creative/creative';
import { creativeAlerts } from './bannerActionPreview';
import type { CreativeModel } from './bannerActionPreview';

const { Text } = Typography;

interface BannerActionPreviewCardProps {
  model: CreativeModel;
  /** Alt text for the creative — the action's own name. */
  name: string;
}

/**
 * The creative as a single static banner. An action is only ever one
 * page, so there is no carousel here: what varies is the phone theme
 * the creative has to hold up against.
 */
export const BannerActionPreviewCard = ({
  model,
  name,
}: BannerActionPreviewCardProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();

  const key = (suffix: string) => `user_catalog.banner_actions.${suffix}`;
  const alerts = creativeAlerts(model);

  return (
    <Card
      title={t(key('preview.title'))}
      extra={
        <Space size={8}>
          <Text type="secondary">
            {t(key('preview.meta'), { ratio: ratioLabel(model.size) })}
          </Text>
          {alerts.length > 0 && (
            <Tag color="gold" style={{ marginInlineEnd: 0 }}>
              {t(key('preview.warning_count'), { count: alerts.length })}
            </Tag>
          )}
        </Space>
      }
    >
      <MobilePreviewProvider>
        <Flex justify="center" style={{ marginBottom: token.margin }}>
          <MobileThemeToggle />
        </Flex>

        <PhoneFrame>
          <div style={{ padding: `0 ${asDeviceWidth(GUTTER)}` }}>
            <CreativeSlot
              imageUrl={model.imageUrl}
              alt={name}
              size={model.size}
            />
          </div>
        </PhoneFrame>

        <MobilePreviewDisclaimer />
      </MobilePreviewProvider>

      {alerts.length > 0 && (
        <>
          <Divider />
          <PreviewAlertList alerts={alerts} keyPrefix={key('preview.alerts')} />
        </>
      )}
    </Card>
  );
};
